import { withI18n } from '@lingui/react'
import { arrayOf, bool, number, shape, string } from 'prop-types'
import { Component } from 'react'
import { compose } from 'redux'

import { LIFESTYLE_PRODUCT } from 'shared/consts/productAttributes'
import {
  DEFAULT_PRESCRIPTION_TYPE,
  PRESCRIPTION_TYPE_FREE,
  PRESCRIPTION_TYPE_INSURANCE,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRESCRIPTION_TYPE_PRIVAT,
  PRODUCT_TEMPLATES,
} from 'shared/consts/productTemplates'
import { TENANT_COM } from 'shared/consts/tenants'
import { isERx, isSaeOnly } from 'shared/experiments/utils/featureFlags'
import store from 'shared/store'
import { RESET_SELECT_VALUES } from 'shared/store/ducks/events'
import { handleVariantClick } from 'views/assets/scripts/prescriptionType'
import Modal from 'views/components/molecules/Modal'
import BuyBoxSticky from 'views/components/organisms/BuyBoxSticky'
import BuyBoxWithVariants from 'views/components/organisms/BuyBoxWithVariants'
import { isLifestyleProduct } from 'views/components/organisms/ProductTemplate/helpers'
import RxSelectModal from 'views/components/organisms/RxSelectModal'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import {
  LIFESYTLE_PRODUCT_INFO_MODAL_ID,
  MEDICAL_DEVICE_INFO_MODAL_ID,
} from 'views/consts'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'
import {
  i18nType,
  ProductVariantType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'

const PRESCRIPTION_SELECT_SELECTOR = '[name="prescriptionType"]'
const GROUP_NAME = 'packageVariants'
const CHANGE_EVENT = 'change'
const EVENT_CHECK_INTERVAL = 250

class ProductVariantsContainer extends Component {
  static getSelects = () => [
    ...document.querySelectorAll(PRESCRIPTION_SELECT_SELECTOR),
  ]
  static propTypes = {
    isMasterPage: bool,
    i18n: i18nType.isRequired,
    variants: arrayOf(shape(ProductVariantType)),
    product: shape({}).isRequired,
    displayNowWidget: bool.isRequired,
    showCountDownPlaceholder: bool.isRequired,
    currentVariantId: string,
    radioButton: bool,
    indicationUrl: string,
    superScripts: shape({}),
    prescriptionType: string,
    updatedPrescriptionType: string,
    productTemplate: string.isRequired,
    isProductVariantOutOfTrade: bool,
    renderCrossSellInsteadOfBuyBox: bool.isRequired,
    maxQuantity: number.isRequired,
    publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
    shippingData: shape({ carriers: arrayOf(shape({})) }),
  }
  static defaultProps = {
    variants: [],
    currentVariantId: null,
    radioButton: false,
    superScripts: {},
    prescriptionType: null,
    updatedPrescriptionType: null,
    isProductVariantOutOfTrade: false,
    shippingData: { carriers: [] },
    isMasterPage: false,
  }

  constructor(props) {
    super(props)

    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
      },
      productTemplate,
      prescriptionType,
      product,
      variants,
    } = props

    const initialPrescriptionType = isLifestyleProduct(
      tenant,
      productTemplate,
      product
    )
      ? PRESCRIPTION_TYPE_PRIVAT
      : prescriptionType

    const defaultPrescriptionType = isLifestyleProduct(
      tenant,
      productTemplate,
      product
    )
      ? PRESCRIPTION_TYPE_PRIVAT
      : DEFAULT_PRESCRIPTION_TYPE[productTemplate]

    this.state = {
      prescriptionType: initialPrescriptionType || defaultPrescriptionType,
      variants,
      groupName: GROUP_NAME,
      cartContainsAnyERXEntries: false,
    }
  }

  async componentDidMount() {
    const { updatedPrescriptionType } = this.props
    const { prescriptionType } = this.state
    // Attaches event to choices instances. This one works for prod env
    // b/c componentDidMount triggers after hydration and in that time
    // choices instance already exist.
    const attachChangeEventOnSelect = () => {
      const selects = ProductVariantsContainer.getSelects()

      selects.forEach(select => {
        select.addEventListener(CHANGE_EVENT, this.changeHandler)
      })

      return Boolean(selects.length)
    }

    // This one is for storybook, in order to make it interactive.
    // We check in 250ms intervals if the choices are instantiated and when
    // they are we attach listeners on them and remove interval
    if (!attachChangeEventOnSelect()) {
      const check = setInterval(() => {
        if (attachChangeEventOnSelect()) {
          clearInterval(check)
        }
      }, EVENT_CHECK_INTERVAL)
    }

    // explicitly ignoring react/no-did-mount-set-state here, as this is the recommended way
    // of hydrating a component which needs different props than the server had
    // (see https://reactjs.org/docs/react-dom.html#hydrate)
    this.setState({
      prescriptionType: updatedPrescriptionType || prescriptionType,
    })

    const cartContainsAnyERXEntries = await this.cartContainsAnyERXEntries()
    this.setState({
      cartContainsAnyERXEntries,
    })
  }

  componentWillUnmount() {
    ProductVariantsContainer.getSelects().forEach(select =>
      select.removeEventListener(CHANGE_EVENT, this.changeHandler)
    )

    // For the prescription select to be reset (when changing the package variant) we need to do this
    store.dispatch({
      type: RESET_SELECT_VALUES,
      selects: [ProductVariantsContainer.getSelects()],
    })
  }

  cartContainsAnyERXEntries = async () => {
    const { cartService, clientFockService } = buildClientAPI()

    const {
      publicRuntimeConfig: {
        pageProperties: { experiments = [] },
        publicConfig: { tenant },
      },
    } = this.props

    if (!isSaeOnly(experiments, tenant)) {
      const {
        data: { hasERxItems },
      } = await clientFockService.fetchUserSessionData()

      return hasERxItems
    }

    const {
      data: { hasERxItems },
    } = await cartService.fetchMetadata()

    return hasERxItems
  }

  changeHandler = event => {
    const {
      target: { value },
    } = event

    this.setState({ prescriptionType: value })

    handleVariantClick(value)
  }

  render() {
    const {
      isMasterPage,
      product,
      currentVariantId,
      displayNowWidget,
      indicationUrl,
      showCountDownPlaceholder,
      isProductVariantOutOfTrade,
      productTemplate,
      radioButton,
      superScripts,
      shippingData,
      renderCrossSellInsteadOfBuyBox,
      maxQuantity,
      publicRuntimeConfig: {
        pageProperties: { experiments = [] },
        publicConfig: { tenant },
      },
      i18n,
    } = this.props
    const { prescriptionType, variants, groupName, cartContainsAnyERXEntries } =
      this.state

    const shouldRenderERxSelectModal =
      tenant === TENANT_COM &&
      isERx(experiments) &&
      [PRESCRIPTION_TYPE_INSURANCE, PRESCRIPTION_TYPE_FREE].includes(
        prescriptionType
      )

    const isRxProductTemplate = productTemplate === PRODUCT_TEMPLATES.RX

    const disableAddToCartButton =
      this.state.prescriptionType === PRESCRIPTION_TYPE_PRIVAT &&
      tenant === TENANT_COM &&
      cartContainsAnyERXEntries

    const shouldRenderMedicalDeviceModal =
      tenant === TENANT_COM &&
      product?.flags?.medicalDevice &&
      [PRESCRIPTION_TYPE_INSURANCE, PRESCRIPTION_TYPE_FREE].includes(
        prescriptionType
      )

    const shouldRenderLifestyleProductModal =
      tenant === TENANT_COM &&
      productTemplate !== PRODUCT_TEMPLATES.OTX &&
      [LIFESTYLE_PRODUCT.TRUE_WITH_EXCEPTIONS, LIFESTYLE_PRODUCT.TRUE].includes(
        product?.lifestyleProduct
      ) &&
      [PRESCRIPTION_TYPE_INSURANCE, PRESCRIPTION_TYPE_FREE].includes(
        prescriptionType
      )

    return (
      <>
        <BuyBoxWithVariants
          isMasterPage={isMasterPage}
          product={product}
          currentVariantId={currentVariantId}
          disableAddToCartButton={disableAddToCartButton}
          displayNowWidget={displayNowWidget}
          indicationUrl={indicationUrl}
          groupName={groupName}
          isProductVariantOutOfTrade={isProductVariantOutOfTrade}
          productTemplate={productTemplate}
          radioButton={radioButton}
          superScripts={superScripts}
          variants={variants}
          prescriptionType={prescriptionType}
          maxQuantity={maxQuantity}
          showCountDownPlaceholder={showCountDownPlaceholder}
          offerId={product.offerId}
          renderCrossSellInsteadOfBuyBox={renderCrossSellInsteadOfBuyBox}
          shouldRenderERxSelectModal={shouldRenderERxSelectModal}
          showSocials={!isRxProductTemplate}
          showOdsWidgets={isRxProductTemplate}
          showAddWishList={
            productTemplate === PRODUCT_TEMPLATES.OTC ||
            (productTemplate === PRODUCT_TEMPLATES.OTX &&
              prescriptionType === PRESCRIPTION_TYPE_NO_PRESCRIPTION)
          }
          shippingData={shippingData}
        />
        <div className="u-hidden" data-clientside-hook="stickyBuyBox">
          <BuyBoxSticky
            product={product}
            prescriptionType={prescriptionType}
            productTemplate={productTemplate}
          />
        </div>
        {tenant === TENANT_COM && isERx(experiments, tenant) && (
          // render modal for every prescriptionType as it's hydrated component and while we check cart meta it should always be rendered
          <RxSelectModal
            offerId={product.offerId}
            prescriptionType={prescriptionType}
            productId={product.id}
          />
        )}
        {shouldRenderMedicalDeviceModal && (
          <Modal
            id={MEDICAL_DEVICE_INFO_MODAL_ID}
            size="medium"
            title={i18n._('medical_device.modal.title')}
            isClientSideControlled
            open
          >
            <TransWithHtml id="medical_device.modal.text" element="p" />
          </Modal>
        )}
        {shouldRenderLifestyleProductModal && (
          <Modal
            id={LIFESYTLE_PRODUCT_INFO_MODAL_ID}
            size="medium"
            title={i18n._('lifestyle_product.modal.title')}
            isClientSideControlled
            open
          >
            <TransWithHtml id="lifestyle_product.modal.text" element="p" />
          </Modal>
        )}
      </>
    )
  }
}

export default compose(
  withShopAndRuntimeConfig,
  withI18n()
)(ProductVariantsContainer)
